import { apiClient, handleApiError } from './client';
import { API_ENDPOINTS } from '../utils/constants';
import type { PaginatedResponse } from '../types/api';
import type { Conversation, Message } from '../types/models';

/**
 * GET /messaging/conversations
 * Returns paginated conversations for the current user.
 */
export async function getConversations(): Promise<PaginatedResponse<Conversation>> {
  try {
    const response = await apiClient.get<PaginatedResponse<Conversation>>(
      API_ENDPOINTS.MESSAGES.CONVERSATIONS
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * GET /messages/?conversation={conversationId}
 * Returns paginated messages for a specific conversation.
 */
export async function getMessages(
  conversationId: string
): Promise<PaginatedResponse<Message>> {
  try {
    const response = await apiClient.get<PaginatedResponse<Message>>(
      API_ENDPOINTS.MESSAGES.MESSAGES(conversationId)
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * POST /messages/
 * Sends a message in a conversation.
 */
export async function sendMessage(
  conversationId: string,
  content: string
): Promise<Message> {
  try {
    const response = await apiClient.post<Message>(API_ENDPOINTS.MESSAGES.BASE, {
      conversation: conversationId,
      content,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * GET /messages/?application={applicationId}
 *
 * Returns the message history for the conversation tied to a job application.
 */
export async function getMessagesByApplication(
  applicationId: string
): Promise<PaginatedResponse<Message>> {
  try {
    const response = await apiClient.get<PaginatedResponse<Message>>(
      `${API_ENDPOINTS.MESSAGES.BASE}?application=${encodeURIComponent(applicationId)}&page_size=100`
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * POST /messages/
 *
 * Sends a new message in the conversation for the given application.
 */
export async function sendMessageByApplication(
  applicationId: string,
  content: string
): Promise<Message> {
  try {
    const response = await apiClient.post<Message>(API_ENDPOINTS.MESSAGES.BASE, {
      application: applicationId,
      content,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}
